import type { Household } from 'src/types/household';
import { useState } from 'react';
import router from 'next/router';
import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from '@savvy-kit/components/dialog';
import { Spinner } from '@savvy-kit/components/spinner';
import InputField from '@savvy-kit/patterns/input-field';
import { FileX, SearchIcon } from 'lucide-react';
import useDebounce from 'src/hooks/use-debounce';
import { useHouseholdSearch } from 'src/utils/household/useHouseholdSearch';

import { HouseholdMenuItem } from '../global-household-search-bar';

type GlobalSearchDialogProps = {
  readonly dialogTriggerRender: React.ReactElement;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
};

function GlobalSearchDialog({ dialogTriggerRender, open, onOpenChange }: GlobalSearchDialogProps) {
  const [searchBarText, setSearchBarText] = useState('');

  const debouncedSearchBarText = useDebounce(searchBarText, 250);
  const trimmedQuery = debouncedSearchBarText.trim();
  const hasQuery = trimmedQuery.length > 0;

  const { data, loading } = useHouseholdSearch({ query: trimmedQuery });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger className="w-full" render={dialogTriggerRender} />
      <DialogContent>
        <DialogTitle className="sr-only">Search Household</DialogTitle>
        <div className="pt-5">
          <InputField
            id="global-search"
            placeholder="Search by household, name, email, or phone"
            aria-label="Search"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchBarText(e.target.value)}
            appendInputAddon={<SearchIcon />}
          />
          <div className="mt-4 h-[300px] flex flex-col">
            {(() => {
              if (hasQuery && loading) {
                return (
                  <div className="flex justify-center items-center pt-8">
                    <Spinner className="size-6 text-primary" />
                  </div>
                );
              }

              if (!hasQuery) {
                return (
                  <div className="border border-light rounded-sm flex flex-col items-center justify-center flex-1 px-6 text-center">
                    <div className="p-3 rounded-md bg-primary-background/50 mb-5">
                      <SearchIcon className="size-6 text-primary" />
                    </div>
                    <span className="text-sm font-semibold mb-1">Find a household</span>
                    <span className="text-sm text-secondary">
                      Try a household name, first or last name, email, or phone number.
                    </span>
                  </div>
                );
              }

              if (!data?.households.edges.length) {
                return (
                  <div className="border border-light rounded-sm h-[120px] flex flex-col items-center justify-center flex-1">
                    <div className="p-3 rounded-md bg-primary-background/50 mb-5">
                      <FileX className="size-6 text-primary" />
                    </div>
                    <span className="text-sm font-semibold mb-1">No records found</span>
                    <span className="text-sm text-secondary">
                      Try a household name, first or last name, email, or phone number.
                    </span>
                  </div>
                );
              }

              return (
                <div className="overflow-y-auto mt-3">
                  <p className="text-xs font-semibold text-secondary mb-1 uppercase">
                    {`${data.households.edges.length} household${data.households.edges.length === 1 ? '' : 's'} found`}
                  </p>
                  {data.households.edges
                    .map((edge) => edge.node)
                    .map((household: Household, index: number) => (
                      <DialogClose
                        key={household.id}
                        className="w-full block text-left"
                        render={
                          <HouseholdMenuItem
                            household={household}
                            index={index}
                            onClick={() => {
                              router.push(`/advisor/dashboard/client/${household.salesforceAccountId}`);
                            }}
                            width="100%"
                          />
                        }
                        nativeButton={false}
                      />
                    ))}
                </div>
              );
            })()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GlobalSearchDialog;
